import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { Fragment, Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import { Decoration, DecorationSet } from '@atlaskit/editor-prosemirror/view';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import type { AIDefinitionsPlugin } from '../../../aiDefinitionsPluginType';
import type { ReadingAidsPopupPropsInState } from '../../../types';
import { KEYWORD_DECORATION_CLASS, KEYWORD_STYLER_CLASS } from '../../../ui/utils/const';
import { getSelectionRect } from '../../../ui/utils/selection';
import {
	handleCloseReadingAidsPopup,
	openReadingAidsPopup,
	readingAidsPopupLoaded,
} from '../commands';
import { getPluginState } from '../plugin-factory';
import type { KeywordMatch, TextGrouping } from '../types';
import {
	createKeywordStyler,
	resetKeywordStylerLoading,
	setKeywordStylerLoading,
} from './keyword-styler';

export function isKeywordNode(node: EventTarget | null): node is HTMLSpanElement {
	return Boolean(
		node && node instanceof HTMLSpanElement && node.classList.contains(KEYWORD_DECORATION_CLASS),
	);
}

// props here is not a complete list, other common props are passed when rendering ReadingAidsPopup
// props are aligned with confluence/next/packages/contextual-reading-aids/src/ReadingAidsAcronymsHighlighter.tsx
// - some of which are from confluence/next/packages/contextual-reading-aids/src/RendererTextHighlighter.tsx
// props for AI define button are from confluence/next/packages/highlight-actions/src/HighlightActions.tsx
function getReadingAidsPopupPropsForKeyword({
	keywordStart,
	keywordEnd,
	editorView,
}: {
	editorView: EditorView;
	keywordEnd: HTMLSpanElement;
	keywordStart: HTMLSpanElement;
}): ReadingAidsPopupPropsInState {
	const range = document.createRange();
	range.setStartBefore(keywordStart);
	range.setEndAfter(keywordEnd);

	const selectionRect = getSelectionRect(range);

	const selectedContext = keywordStart.parentElement;
	const additionalContext = selectedContext
		? selectedContext.textContent || selectedContext.innerText
		: '';

	return {
		selectedText: range.toString(),
		additionalContext,
		selectionRange: range,
		selectionRect,
		onResult: () => {},
		onLoad: () => {
			resetKeywordStylerLoading();
			readingAidsPopupLoaded()(editorView.state, editorView.dispatch);
		},
		onClose: () => {
			handleCloseReadingAidsPopup({ editorView });
		},
		isAutohighlighted: true,
		isEditor: true,
	};
}

function handleOpenReadingAidsPopup({
	event,
	editorView,
	api,
	id,
}: {
	api: ExtractInjectionAPI<AIDefinitionsPlugin> | undefined;
	editorView: EditorView;
	event: MouseEvent;
	id: string;
}): boolean {
	// there might be multiple consecutive keyword inline decorations if there are other decorations on the keyword text
	const keywordDecorationArr = document.querySelectorAll(`.${KEYWORD_DECORATION_CLASS}-${id}`);
	const keywordStart = keywordDecorationArr[0];
	const keywordEnd = keywordDecorationArr[keywordDecorationArr.length - 1];
	if (isKeywordNode(keywordStart) && isKeywordNode(keywordEnd)) {
		const readingAidsPopupProps = getReadingAidsPopupPropsForKeyword({
			keywordStart,
			keywordEnd,
			editorView,
		});

		setKeywordStylerLoading(id);
		openReadingAidsPopup({
			api,
			source: 'auto-highlight-click',
			keywordId: id,
			readingAidsPopupProps,
		})(editorView.state, editorView.dispatch);

		event.preventDefault();
		event.stopPropagation();
		return true;
	}
	return false;
}

/**
 * when click keyword, show ReadingAidsPopup
 * - only show ReadingAidsPopup if there's no ReadingAidsPopup open
 * - currently, when there is a ReadingAidsPopup open, the renderer won't open another one
 * - if we want to close the current one and open a new one instead in the future, we can refine the check
 */
export const onClickKeywordDecoration = ({
	event,
	editorView,
	api,
}: {
	api: ExtractInjectionAPI<AIDefinitionsPlugin> | undefined;
	editorView: EditorView;
	event: MouseEvent;
}): boolean => {
	const { target } = event;
	const aiDefinitionsPluginState = getPluginState(editorView.state);
	const { isReadingAidsPopupOpen, currentKeywordId, isReadingAidsPopupLoading } =
		aiDefinitionsPluginState;

	// click keyword
	if (isKeywordNode(target) && target.dataset.id) {
		const id = target.dataset.id;
		// click the same keyword while loading, do nothing
		if (id === currentKeywordId && isReadingAidsPopupLoading) {
			// prevent the click event from propagating to the popup to close it
			event.stopPropagation();
			event.preventDefault();
			return true;
		}
		// only open popup if there's no popup open
		if (!isReadingAidsPopupOpen) {
			return handleOpenReadingAidsPopup({ event, editorView, api, id });
		}
	}

	// all other cases, if there is a ReadingAidsPopup open, close it
	if (isReadingAidsPopupOpen) {
		handleCloseReadingAidsPopup({ editorView });
	}

	return false;
};

// the confluence example is setup to match I&<>"'`.-D as a keyword, visit the example to see the generated ID
function encodeID(s: string) {
	if (s === '') {
		return '_';
	}

	// Ignored via go/ees005
	// eslint-disable-next-line require-unicode-regexp
	return s.replace(/[^a-zA-Z0-9-]/g, function (match) {
		return '_' + match[0].charCodeAt(0).toString(16);
	});
}

/**
 * If other decorations are applied on part of the keyword text.
 * - like comments / syntax and grammar checks / proactive AI checks / other unknown decorations
 * For example, API is matched by a keyword decoration, and a comment exists on the middle `P`.
 * Then there will be three consecutive span `<span>A</span>`, `<span>P</span>, `<span>I</span>`.
 * Which means there will be three consecutive keyword inline decorations.
 *
 * If we apply the linear-gradient bottom border on the inline decoration spans,
 * there will be three consecutive linear-gradient bottom-border like this
 * https://files.slack.com/files-pri/TE8HJA7RS-F07UKRQ7JCT/image.png
 *
 * So we have to use a separate widget decoration to render the linear-gradient bottom border
 */
export const createKeywordDecorations = ({
	keywordMatch,
}: {
	keywordMatch: KeywordMatch;
}): Decoration[] => {
	const { from, to, text } = keywordMatch;
	const id = `${from}-${to}-${encodeID(text)}`;
	return [
		Decoration.inline(
			from,
			to,
			{
				class: `${KEYWORD_DECORATION_CLASS} ${KEYWORD_DECORATION_CLASS}-${id}`,
				'data-id': id,
				'data-testid': KEYWORD_DECORATION_CLASS,
			},
			{ type: 'keyword', key: `${KEYWORD_DECORATION_CLASS}-${id}` },
		),
		Decoration.widget(
			to,
			() => {
				return createKeywordStyler({ id });
			},
			{
				type: 'keywordStyler',
				key: `${KEYWORD_STYLER_CLASS}-${id}`,
				stopEvent: () => true,
				ignoreSelection: true,
			},
		),
	];
};

/**
 * Exclude the following nodes from keyword matching
 * - code
 * - status
 */
function excludeNode(node: PMNode): boolean {
	const { name } = node.type;
	return name === 'codeBlock' || name === 'status';
}

/**
 * Check if the text node conflict with keyword decoration
 * - annotation mark
 * - link mark
 * - code mark
 */
function isTextNodeConflictWithKeywordDecoration(node: PMNode): boolean {
	return (
		node.isText &&
		node.marks.some((mark) => {
			const { name } = mark.type;
			return name === 'annotation' || name === 'link' || name === 'code';
		})
	);
}

// code from findMatches in editor-plugin-find-replace/src/utils/index.ts
export function findKeywordMatches({
	content,
	keywordRegex,
	contentIndex = 0,
}: {
	content: PMNode | Fragment;
	contentIndex?: number;
	keywordRegex: RegExp;
}): KeywordMatch[] {
	// each keyword is matched only once
	const set = new Set<string>();
	const matches: KeywordMatch[] = [];
	let textGrouping: TextGrouping | null = null;

	const collectMatch = (textGrouping: TextGrouping | null) => {
		if (!textGrouping) {
			return;
		}
		const { text, pos: relativePos } = textGrouping;
		const pos = contentIndex + relativePos;

		const regexMatches = text.matchAll(keywordRegex);
		for (const regexMatch of regexMatches) {
			const text = regexMatch[0];
			const textKey = keywordRegex.ignoreCase ? text.toUpperCase() : text;
			if (!set.has(textKey)) {
				set.add(textKey);
				matches.push({
					from: pos + regexMatch.index,
					to: pos + regexMatch.index + text.length,
					text,
				});
			}
		}
	};

	content.descendants((node, pos) => {
		if (excludeNode(node)) {
			return false;
		}

		if (node.isText && node.text) {
			let text = node.text;

			if (isTextNodeConflictWithKeywordDecoration(node)) {
				text = ' '.repeat(text.length);
			}

			if (textGrouping === null) {
				textGrouping = { pos, text };
			} else {
				textGrouping.text += text;
			}
		} else {
			if (textGrouping) {
				collectMatch(textGrouping);
			}
			textGrouping = null;
		}
	});
	// if there's a dangling text grouping and no non-text node to trigger collectMatch, manually collectMatch
	if (textGrouping) {
		collectMatch(textGrouping);
	}

	return matches;
}

export function createKeywordDecorationsForDoc({
	editorView,
	keywordRegex,
}: {
	editorView: EditorView;
	keywordRegex: RegExp;
}): DecorationSet {
	let decorations = DecorationSet.empty;

	const keywordMatches = findKeywordMatches({
		content: editorView.state.doc,
		keywordRegex,
	});

	for (const keywordMatch of keywordMatches) {
		const keywordDecorations = createKeywordDecorations({ keywordMatch });
		decorations = decorations.add(editorView.state.doc, keywordDecorations);
	}

	return decorations;
}
